import React, { useState, useEffect, FC } from "react";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalBody,
  UncontrolledDropdown,
  Badge,
  Col,
  Row,
  UncontrolledTooltip,
} from "reactstrap";
import { Icon, Button, TooltipComponent } from "../../../components/Component";
import Swal from "sweetalert2";
import locationMasterApis from "../../../api/master/locationMaster";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import TransactionFlowDefaultTemplate from "../../others/transactionFlowDefaultTemplate";
import dayjs from "dayjs";
import LocationMasterForm from "./LocationMasterForm";
import FormatDate from "../DateFormater";
import sitebreakdownMasterApis from "../../../api/master/sitebreakdownMaster";
import { normalizeStatus } from "../../../utils/Utils";

const LocationMaster: FC = () => {
  const [refreshToggle, setRefreshToggle] = useState(false);
  const { data: accessibility } = useSelector(
    (state: any) => state.accessibility
  );
  const [allowedActions, setAllowedActions] = useState<any>({
    add: 0,
    view: 0,
    edit: 0,
    approve: 0,
    reject: 0,
    cancel: 0,
  });

  // Modal states
  const [isOpen, setIsOpen] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [detail, setDetail] = useState<any>({});

  const [popUpContent, setPopUpContent] = useState<JSX.Element | null>(null);
  const [siteProjects, setSiteProjects] = useState<any[]>([]);

  useEffect(() => {
    if (accessibility) {
      try {
        const { add, view, edit, approve, reject, cancel } =
          accessibility["Master"];
        setAllowedActions({ add, view, edit, approve, reject, cancel });
      } catch (error) {
        console.log(error);
      }
    }
  }, [accessibility]);

  const navigate = useNavigate();

  // Fetch site projects for filter
  const fetchSiteProjects = async () => {
    try {
      const response =
        await sitebreakdownMasterApis.fetchSiteBreakDownDropDown();
      if (response?.status) {
        const projList = response.data.map((proj: any) => ({
          label: `${proj.projectName} : ${proj.siteName}`,
          value: proj.id,
        }));
        setSiteProjects(projList);
      }
    } catch (error) {
      console.error("Error fetching site projects:", error);
    }
  };

  const handleStatusChange = async (
    locationId: any,
    action: "activate" | "inActivate"
  ) => {
    const actionText = action === "activate" ? "Activate" : "inActivate";
    const status = action === "activate" ? "Active" : "InActive";

    Swal.fire({
      title: `Are you sure?`,
      text: `You want to ${actionText} this location!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: `Yes, ${actionText} it!`,
      allowOutsideClick: false,
      buttonsStyling: false,
      customClass: {
        confirmButton: "btn btn-primary",
        cancelButton: "btn btn-secondary",
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response =
            action === "activate"
              ? await locationMasterApis.allowActiveStatus(locationId)
              : await locationMasterApis.removeActiveStatus(locationId);

          if (response.status) {
            await Swal.fire(
              "Success!",
              `Location has been ${
                action === "activate" ? "Activated" : "Inactivated"
              }.`,
              "success"
            );
            refreshData();
          } else {
            await Swal.fire("Error", response.message, "error");
          }
        } catch (error: any) {
          await Swal.fire("Error", error.message, "error");
        }
      }
    });
  };

  const handleDelete = (id: any) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      allowOutsideClick: false,
      buttonsStyling: false,
      customClass: {
        confirmButton: "btn btn-primary",
        cancelButton: "btn btn-secondary",
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        await deleteLocationById(id);
      }
    });
  };

  const deleteLocationById = async (id: any) => {
    try {
      const response = await locationMasterApis.deleteLocationById(id);
      // Handle both success and error responses
      const status = response?.status ?? response?.data?.status;
      const message = response?.message ?? response?.data?.message;

      if (status) {
        await Swal.fire(
          "Deleted!",
          message || "Location has been deleted.",
          "success"
        );
        refreshData();
      } else {
        await Swal.fire(
          "Error",
          message || "Failed to delete location.",
          "error"
        );
      }
    } catch (error: any) {
      const errorMessage =
        error?.message ??
        error?.response?.data?.message ??
        "An error occurred while deleting location.";
      await Swal.fire("Error", errorMessage, "error");
    }
  };

  const refreshData = () => {
    setRefreshToggle((prev) => !prev);
  };

  const loadDetail = async (id: any) => {
    try {
      const response = await locationMasterApis.fetchLocationById(id);
      if (response && response.status) {
        setDetail(response.data);
      } else {
        console.error("Failed to fetch location details");
        setDetail({ id });
      }
    } catch (error) {
      console.error("Error fetching location details:", error);
      setDetail({ id });
    }
  };

  const handleModelPopUp = (id?: any, type?: string) => {
    const content = (
      <LocationMasterForm
        formAction={type}
        id={id}
        toggleModelPopUp={() => {
          setIsOpen(false);
        }}
        refreshData={refreshData}
      />
    );
    setPopUpContent(content);
    setIsOpen(true);
  };

  // Define select filters for the template
  const selectFilters = [
    {
      title: "Select Site Structure",
      placeholder: "Select Site Structure",
      options: siteProjects,
      onChange: (value: any) => {
        console.log("Site Structure selected:", value);
      },
    },
    {
      title: "Status",
      placeholder: "Select Status",
      options: [
        { label: "Active", value: "Active" },
        { label: "Inactive", value: "Inactive" },
      ],
      onChange: (value: any) => {
        console.log("Status selected:", value);
      },
    },
  ];

  const columns = [
    {
      title: "Location ID",
      dataIndex: "siteStructureLocationSequenceId",
      key: "siteStructureLocationSequenceId",
      width: 120,
    },
    {
      title: "Project",
      dataIndex: "projectName",
      key: "projectName",
      width: 150,
    },
    {
      title: "Location Name",
      dataIndex: "locationName",
      key: "locationName",
      width: 200,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 120,
      render: (text: any) => (
        <span
          className={`tb-status text-${
            text === "Stage" ? "primary" : "warning"
          }`}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 200,
      render: (text: string, _: any, index: number) => {
        if (!text) return "N/A";

        return (
          <div className="d-flex align-items-center">
            <span id={`desc-tooltip-${index}`}>
              {text.length > 25 ? `${text.substring(0, 25)}...` : text}
            </span>

            {text.length > 25 && (
              <TooltipComponent
                iconClass="info-circle text-info ms-1"
                icon="info"
                id={`desc-tooltip-${index}`}
                direction="top"
                text={text}
                containerClassName=""
              />
            )}
          </div>
        );
      },
    },
    {
      title: "Rank",
      dataIndex: "sequenceRank",
      key: "sequenceRank",
      width: 90,
      render: (text: any) => text || "N/A",
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (text: any) => <span>{dayjs(text).format("DD-MMM-YYYY")}</span>,
    },
    {
      title: "Created By",
      dataIndex: "createdByName",
      key: "createdByName",
      width: 150,
      render: (text: any) => text || "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (text: any) => {
        const normalizedStatus = normalizeStatus(text);
        return (
          <span
            className={`tb-status text-${
              normalizedStatus === "Active" ? "success" : "danger"
            }`}
          >
            {normalizedStatus}
          </span>
        );
      },
    },
    {
      title: "Details",
      dataIndex: "action",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text: any, record: any) => {
        const normalizedStatus = normalizeStatus(record.status);
        const canView = allowedActions?.view;
        const canEdit =
          allowedActions?.edit && normalizeStatus(record.status) === "Active";
        const canToggleStatus = true;
        const canDelete = allowedActions?.cancel;
        const isActive = normalizedStatus === "Active";

        const availableActions = [
          canView,
          canEdit,
          canToggleStatus,
          canDelete,
        ].filter(Boolean).length;

        return (
          <div
            className="details-cell"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "40px",
              gap: "8px",
            }}
          >
            {canEdit && (
              <div className="edit-hover-wrapper">
                <a
                  href="#edit"
                  className="btn btn-icon btn-trigger edit-hover-icon"
                  id={`edit-${record.id}`}
                  onClick={(ev) => {
                    ev.preventDefault();
                    handleModelPopUp(record.id, "edit");
                  }}
                >
                  <Icon name="edit-alt-fill"></Icon>
                </a>

                <TooltipComponent
                  id={`edit-tooltip-${record.id}`}
                  direction="top"
                  icon=""
                  text="Edit"
                  containerClassName=""
                  target={`edit-${record.id}`}
                  iconClass={undefined}
                />
              </div>
            )}

            {(canView || canEdit || canToggleStatus || canDelete) && (
              <UncontrolledDropdown direction="start">
                <DropdownToggle
                  tag="button"
                  className="btn btn-sm btn-icon btn-light"
                  style={{
                    border: "none",
                    background: "none",
                    position: "relative",
                    zIndex: 1,
                    color: "#526484",
                  }}
                >
                  <Icon name="more-h" style={{ color: "inherit" }}></Icon>
                </DropdownToggle>
                <DropdownMenu
                  end
                  style={{
                    position: "fixed",
                    minWidth: "160px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    border: "1px solid #e5e5e5",
                    zIndex: 9999,
                    inset: "auto auto auto auto !important",
                  }}
                  container="body"
                >
                  <ul className="link-list-opt no-bdr" style={{ margin: 0 }}>
                    {/* view option */}
                    {canView && (
                      <li>
                        <DropdownItem
                          tag="a"
                          href="#view"
                          onClick={async (ev) => {
                            ev.preventDefault();
                            await loadDetail(record.id);
                            setViewModal(true);
                          }}
                          style={{
                            padding: "8px 16px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Icon name="focus" className="me-2" />
                          <span>View Details</span>
                        </DropdownItem>
                      </li>
                    )}

                    {/* Edit option - only for active records */}
                    {canEdit && (
                      <li>
                        <DropdownItem
                          tag="a"
                          href="#edit"
                          onClick={(ev) => {
                            ev.preventDefault();
                            handleModelPopUp(record.id, "edit");
                          }}
                          style={{
                            padding: "8px 16px",
                            display: "flex",
                            alignItems: "center",
                            textDecoration: "none",
                          }}
                        >
                          <Icon name="edit" className="me-2"></Icon>
                          <span>Edit</span>
                        </DropdownItem>
                      </li>
                    )}

                    {/* Status toggle - always show one of them */}
                    {canToggleStatus && (
                      <>
                        {isActive ? (
                          <li>
                            <DropdownItem
                              tag="a"
                              href="#inactivate"
                              onClick={(ev) => {
                                ev.preventDefault();
                                handleStatusChange(record.id, "inActivate");
                              }}
                              style={{
                                padding: "8px 16px",
                                display: "flex",
                                alignItems: "center",
                                textDecoration: "none",
                              }}
                            >
                              <Icon name="cross-circle" className="me-2"></Icon>
                              <span>Inactivate</span>
                            </DropdownItem>
                          </li>
                        ) : (
                          <li>
                            <DropdownItem
                              tag="a"
                              href="#activate"
                              onClick={(ev) => {
                                ev.preventDefault();
                                handleStatusChange(record.id, "activate");
                              }}
                              style={{
                                padding: "8px 16px",
                                display: "flex",
                                alignItems: "center",
                                textDecoration: "none",
                              }}
                            >
                              <Icon name="check-circle" className="me-2"></Icon>
                              <span>Activate</span>
                            </DropdownItem>
                          </li>
                        )}
                      </>
                    )}

                    {/* Delete option */}
                    {canDelete && (
                      <li>
                        <DropdownItem
                          tag="a"
                          href="#delete"
                          onClick={(ev) => {
                            ev.preventDefault();
                            handleDelete(record.id);
                          }}
                          style={{
                            padding: "8px 16px",
                            display: "flex",
                            alignItems: "center",
                            textDecoration: "none",
                          }}
                        >
                          <Icon name="trash" className="me-2"></Icon>
                          <span>Delete</span>
                        </DropdownItem>
                      </li>
                    )}
                  </ul>
                </DropdownMenu>
              </UncontrolledDropdown>
            )}
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    fetchSiteProjects();
  }, []);

  return (
    <>
      <TransactionFlowDefaultTemplate
        moduleKey="locationmaster-list"
        title="Location Master"
        columns={columns}
        selectFilters={selectFilters}
        extra={{
          tableScrollX: 1200,
          excelExport: true,
          style: { position: "relative" },
        }}
        refresh={refreshToggle}
        topRightJSX={
          allowedActions?.add ? (
            <Button
              color="primary"
              onClick={() => handleModelPopUp(null, "add")}
            >
              <Icon name="plus"></Icon>
              <span>Add</span>
            </Button>
          ) : (
            <></>
          )
        }
      />

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isOpen}
        toggle={() => setIsOpen(false)}
        className="modal-dialog-centered"
        size="lg"
        backdrop="static"
        style={{ maxWidth: 800 }}
      >
        <a
          href="#cancel"
          onClick={(ev) => {
            ev.preventDefault();
            setIsOpen(false);
          }}
          className="close"
        >
          <Icon name="cross-sm"></Icon>
        </a>
        <ModalBody>{popUpContent}</ModalBody>
      </Modal>

      {/* View Details Modal */}
      <Modal
        isOpen={viewModal}
        toggle={() => setViewModal(false)}
        className="modal-dialog-centered"
        size="lg"
        backdrop="static"
      >
        <ModalBody>
          <a
            href="#cancel"
            onClick={(ev) => {
              ev.preventDefault();
              setViewModal(false);
            }}
            className="close"
          >
            <Icon name="cross-sm"></Icon>
          </a>
          <div className="nk-modal-head">
            <h4 className="nk-modal-title title">Location Details</h4>
          </div>
          <div className="nk-tnx-details mt-sm-3">
            <Row className="gy-3">
              <Col lg={6}>
                <span className="sub-text">ID</span>
                <span className="caption-text">{detail.siteStructureLocationSequenceId}</span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Project</span>
                <span className="caption-text">{detail.projectName}</span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Location Name</span>
                <span className="caption-text">{detail.locationName}</span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Type</span>
                <Badge
                  color={detail.type === "Stage" ? "primary" : "warning"}
                  size="md"
                >
                  {detail.type}
                </Badge>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Description</span>
                <span className="caption-text">
                  {detail.description || "N/A"}
                </span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Rank</span>
                <span className="caption-text">
                  {detail.sequenceRank || "N/A"}
                </span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Status</span>
                <Badge
                  color={
                    normalizeStatus(detail.status) === "Active"
                      ? "success"
                      : "danger"
                  }
                  size="md"
                >
                  {normalizeStatus(detail.status)}
                </Badge>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Created Date</span>
                <span className="caption-text">
                  {detail.createdAt ? FormatDate(detail.createdAt) : "N/A"}
                </span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Created By</span>
                <span className="caption-text">
                  {detail.createdByName || "N/A"}
                </span>
              </Col>
            </Row>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default LocationMaster;
